import React, { useEffect, useMemo, useState } from 'react';
import LiveBackupManager from '../../services/LiveBackupManager.js';
import Button from '../ui/Button';
import HelpIcon from '../Documentation/HelpIcon';

import ScheduleOptionsMenu from '../ScheduleOptionsMenu';
import ClassSchedulerTimeFrameSelector from '../ClassSchedulerTimeFrameSelector';
import { IoCalendarClearOutline, IoPersonOutline, IoHomeOutline, IoDocumentsOutline, IoChevronDown } from 'react-icons/io5';
import { IoMdBook, IoMdMicrophone } from 'react-icons/io';
import { FaRegClock } from 'react-icons/fa';
import { MdSettingsBackupRestore } from 'react-icons/md';

const ClassSchedulerHeader = ({
    rooms,
    isLiveMode,
    currentDraftName,
    changeLogLength,
    openChangeLogModal,
    isAdmin,
    selectedYearForView,
    isImporting,
    isExporting,
    importError,
    importSuccess,
    isLoading,
    error,
    success,
    setError,
    setSuccess,
    setIsLoading,
    draftManager,
    onToggleDebug,
    onModeChange,
    onDraftChange,
    onSaveCurrent,
    onPublishDraft,
    onFileUpload,
    onExport,
    currentView,
    setCurrentView,
    timePeriods,
    selectedPeriodId,
    onPeriodChange,
    availableYears,
    onYearChange,
    onMonthChange,
    selectedMonth,
    onWeekChange,
    selectedWeek,
    textFilterQuery,
    setTextFilterQuery,
    allRoomsForGrid,
    selectedRoomForView,
    setSelectedRoomForView,
    teachers,
    selectedTeacherForView,
    setSelectedTeacherForView,
    entryTypeFilters,
    setEntryTypeFilters
}) => {
    const entryTypeOptions = useMemo(() => ([
        { key: 'class', label: 'Classes', Icon: IoMdBook },
        { key: 'meeting', label: 'Meetings', Icon: FaRegClock },
        { key: 'event', label: 'Events', Icon: IoMdMicrophone }
    ]), []);

    const handleToggleEntryType = (typeKey) => {
        if (typeof setEntryTypeFilters !== 'function') return;
        setEntryTypeFilters((prev) => {
            const current = prev ?? {};
            return {
                ...current,
                [typeKey]: !(current?.[typeKey] ?? true)
            };
        });
    };

    return (
        <div className="flex-none p-4 bg-white border-b border-gray-200 shadow-sm">
            {rooms.length > 0 ? (
                <>
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                {isLiveMode ? (
                                    <>
                                        <LiveModeDropdown
                                            draftManager={draftManager}
                                            onModeChange={onModeChange}
                                            onDraftChange={onDraftChange}
                                            selectedYearForView={selectedYearForView}
                                            setError={setError}
                                            setSuccess={setSuccess}
                                            setIsLoading={setIsLoading}
                                        />
                                        {/* Backups Dropdown (visible in Live mode) */}
                                        <BackupsDropdown
                                            draftManager={draftManager}
                                            onOpenBackup={(name) => LiveBackupManager.openBackupAsDraft(name, draftManager, onModeChange)}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <DraftModeDropdown
                                            currentDraftName={currentDraftName}
                                            draftManager={draftManager}
                                            onModeChange={onModeChange}
                                            onDraftChange={onDraftChange}
                                            selectedYearForView={selectedYearForView}
                                            setError={setError}
                                            setSuccess={setSuccess}
                                            setIsLoading={setIsLoading}
                                        />
                                    </>
                                )}

                                <HelpIcon
                                    documentationId="schedule-draft-live-mode"
                                    size={16}
                                    position="inline"
                                    className="ml-1"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => {
                                    setCurrentView('calendar');
                                }}
                                className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${currentView === 'calendar'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <IoCalendarClearOutline className="w-4 h-4" />
                                Calendar View
                            </button>
                            <button
                                onClick={() => {
                                    setCurrentView('room');
                                }}
                                className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${currentView === 'room'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <IoHomeOutline className="w-4 h-4" />
                                Room View
                            </button>
                            <button
                                onClick={() => {
                                    setCurrentView('teacher');
                                }}
                                className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${currentView === 'teacher'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <IoPersonOutline className="w-4 h-4" />
                                Teacher View
                            </button>
                            <button
                                onClick={() => {
                                    setCurrentView('drafts');
                                }}
                                className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${currentView === 'drafts'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <IoDocumentsOutline className="w-4 h-4" />
                                Draft View
                            </button>
                        </div>

                        <div className="flex items-center gap-4">


                            {isAdmin && (
                                <ScheduleOptionsMenu
                                    currentYear={selectedYearForView}
                                    isLiveMode={isLiveMode}
                                    currentDraftName={currentDraftName}
                                    onModeChange={onModeChange}
                                    onDraftChange={onDraftChange}
                                    onSaveCurrent={onSaveCurrent}
                                    onPublishDraft={onPublishDraft}
                                    onFileUpload={onFileUpload}
                                    onExport={onExport}
                                    isImporting={isImporting}
                                    isExporting={isExporting}
                                    importError={importError}
                                    importSuccess={importSuccess}
                                    isLoading={isLoading}
                                    error={error}
                                    success={success}
                                    setError={setError}
                                    setSuccess={setSuccess}
                                    setIsLoading={setIsLoading}
                                    draftManager={draftManager}
                                    onToggleDebug={onToggleDebug}
                                />
                            )}
                        </div>
                    </div>

                    {currentView !== 'drafts' && (
                    <div className="pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-4">
                            <ClassSchedulerTimeFrameSelector
                                timePeriods={timePeriods}
                                selectedPeriodId={selectedPeriodId}
                                onPeriodChange={onPeriodChange}
                                selectedYear={selectedYearForView}
                                availableYears={availableYears}
                                onYearChange={onYearChange}
                                onMonthChange={onMonthChange}
                                selectedMonth={selectedMonth}
                                onWeekChange={onWeekChange}
                                selectedWeek={selectedWeek}
                                currentView={currentView}
                            />

                            <div className="relative w-full max-w-xl">
                                <input
                                    type="text"
                                    value={textFilterQuery}
                                    onChange={(e) => setTextFilterQuery(e.target.value)}
                                    placeholder="Filter: groups, teacher, subject, description, host"
                                    className="w-full px-3 py-1 pr-28 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <div className="absolute inset-y-0 right-1 flex items-center gap-1">
                                    {entryTypeOptions.map(({ key, label, Icon }) => {
                                        const isActive = entryTypeFilters?.[key] ?? true;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => handleToggleEntryType(key)}
                                                className={`p-1 text-sm rounded-md transition-colors flex items-center justify-center ${isActive
                                                    ? 'text-blue-700'
                                                    : 'text-gray-400 hover:text-gray-600'}`}
                                                aria-pressed={isActive}
                                                aria-label={label}
                                                title={`Toggle on/off ${label.toLowerCase()}`}
                                            >
                                                <Icon className="w-4 h-4" aria-hidden="true" />
                                                <span className="sr-only">{label}</span>
                                            </button>
                                        );
                                    })}
                                    {textFilterQuery && (
                                        <button
                                            onClick={() => setTextFilterQuery('')}
                                            className="p-2 text-gray-400 hover:text-gray-600"
                                            aria-label="Clear filter"
                                            type="button"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>

                            {currentView === 'room' && (
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-gray-700">Room:</label>
                                    <select
                                        value={typeof selectedRoomForView === 'object' && selectedRoomForView !== null ? selectedRoomForView.name : (selectedRoomForView || '')}
                                        onChange={(e) => {
                                            const selectedRoom = allRoomsForGrid.find(room => room.name === e.target.value);
                                            setSelectedRoomForView(selectedRoom || e.target.value);
                                        }}
                                        className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-48"
                                    >
                                        <option value="">Select a room</option>
                                        {allRoomsForGrid.map(room => (
                                            <option key={room.id || room.name} value={room.name}>{room.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {currentView === 'teacher' && (
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-gray-700">Teacher:</label>
                                    <select
                                        value={selectedTeacherForView || ''}
                                        onChange={(e) => setSelectedTeacherForView(e.target.value)}
                                        className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-48"
                                    >
                                        <option value="">Select a teacher</option>
                                        {teachers.map(teacher => (
                                            <option key={teacher.id} value={teacher.name}>{teacher.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                    )}
                </>
            ) : (
                <div className="flex justify-between items-center">
                    <h1 className="text-xl font-semibold text-gray-800">Schedule Setup</h1>
                </div>
            )}
        </div>
    );
};

export default ClassSchedulerHeader;

// --- Internal Draft Mode Dropdown Component ---
const DraftModeDropdown = ({
    currentDraftName,
    draftManager,
    onModeChange,
    onDraftChange,
    selectedYearForView,
    setError,
    setSuccess,
    setIsLoading
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [newName, setNewName] = useState('');

    const handleGoToLive = () => {
        if (draftManager) {
            draftManager.setLiveMode();
        }
        onModeChange(true, null);
        setIsOpen(false);
        if (setSuccess) setSuccess('Switched to live schedule');
        if (setSuccess) setTimeout(() => setSuccess(null), 3000);
    };

    const handleRename = async () => {
        if (!newName.trim()) {
            if (setError) setError('Please enter a new draft name');
            return;
        }

        if (newName.trim() === currentDraftName) {
            setIsRenaming(false);
            setNewName('');
            return;
        }

        if (setIsLoading) setIsLoading(true);
        try {
            // Use the new rename API endpoint
            await draftManager.renameDraft(currentDraftName, newName.trim());

            // Update the current draft name in the parent component
            onDraftChange(newName.trim());

            if (setSuccess) setSuccess(`Draft renamed from "${currentDraftName}" to "${newName.trim()}"`);
            if (setSuccess) setTimeout(() => setSuccess(null), 3000);

            setIsRenaming(false);
            setNewName('');
            setIsOpen(false);
        } catch (err) {
            if (setError) setError('Failed to rename draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg border border-blue-200 hover:bg-blue-200 transition-colors"
                title={`Draft: ${currentDraftName || 'Unnamed Draft'}`}
            >
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="font-semibold text-sm truncate" style={{ maxWidth: '300px' }}>
                    Draft: {currentDraftName || 'Unnamed Draft'}
                </span>
                <IoChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <>
                    <div className="absolute mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                        <div className="py-1">
                            <button
                                onClick={handleGoToLive}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                                Go to Live
                            </button>
                            <div className="border-t border-gray-100 my-1"></div>
                            <button
                                onClick={() => {
                                    setIsRenaming(true);
                                    setNewName(currentDraftName || '');
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                                Rename
                            </button>
                        </div>
                    </div>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                </>
            )}

            {/* Rename Modal */}
            {isRenaming && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96">
                        <h3 className="text-lg font-semibold mb-4">Rename Draft</h3>
                        <input
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Enter new draft name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                            autoFocus
                        />
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => {
                                    setIsRenaming(false);
                                    setNewName('');
                                }}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRename}
                                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Rename
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Internal Live Mode Dropdown Component ---
const LiveModeDropdown = ({
    draftManager,
    onModeChange,
    onDraftChange,
    selectedYearForView,
    setError,
    setSuccess,
    setIsLoading
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadDrafts = async () => {
        if (!draftManager) return;
        setLoading(true);
        try {
            const draftList = await draftManager.fetchDrafts();
            // Exclude automatic backups from normal drafts list
            const filtered = (draftList || []).filter(d => !(d?.draft_name && LiveBackupManager.isBackupName(String(d.draft_name))));
            setDrafts(filtered);
        } catch (e) {
            console.error('Error loading drafts:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadDrafts();
        }
    }, [isOpen]);

    // Listen to draft manager changes to keep drafts list updated
    useEffect(() => {
        if (draftManager) {
            const handleDraftManagerChange = (state) => {
                if (state.drafts) {
                    // Exclude automatic backups from normal drafts list
                    const filtered = (state.drafts || []).filter(d => !(d?.draft_name && LiveBackupManager.isBackupName(String(d.draft_name))));
                    setDrafts(filtered);
                }
            };

            draftManager.addListener(handleDraftManagerChange);

            // Cleanup listener on unmount
            return () => {
                draftManager.removeListener(handleDraftManagerChange);
            };
        }
    }, [draftManager]);

    const handleLoadDraft = async (draftName) => {
        if (setIsLoading) setIsLoading(true);
        try {
            if (draftManager) {
                draftManager.setDraftMode(draftName);
            }
            onModeChange(false, draftName);
            if (setSuccess) setSuccess(`Loaded draft "${draftName}"`);
            if (setSuccess) setTimeout(() => setSuccess(null), 3000);
            setIsOpen(false);
        } catch (err) {
            if (setError) setError('Failed to load draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-lg border border-green-200 hover:bg-green-200 transition-colors"
            >
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-semibold text-sm">Live Schedule</span>
                <IoChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <>
                    <div className="absolute mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                        <div className="p-2">
                            <div className="px-3 py-2 text-xs text-gray-500 uppercase tracking-wide">Load Draft</div>
                            {loading && (
                                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                            )}
                            {!loading && drafts.length === 0 && (
                                <div className="px-3 py-2 text-sm text-gray-500">No drafts available</div>
                            )}
                            <div className="max-h-60 overflow-auto">
                                {drafts.map((draft, idx) => (
                                    <button
                                        key={`${draft.draft_name}-${idx}`}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                        onClick={() => handleLoadDraft(draft.draft_name)}
                                        title={draft.draft_name}
                                    >
                                        <div className="font-medium truncate">{draft.draft_name}</div>
                                        {draft.last_modified && (
                                            <div className="text-xs text-gray-500">
                                                {new Date(draft.last_modified).toLocaleString()}
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                </>
            )}
        </div>
    );
};

// --- Internal Backups Dropdown Component ---
const BackupsDropdown = ({ draftManager, onOpenBackup }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [backups, setBackups] = useState([]);

    const loadBackups = async () => {
        if (!draftManager) return;
        setLoading(true);
        try {
            const onlyBackups = await LiveBackupManager.listBackups(draftManager);
            setBackups(onlyBackups);
        } catch (e) {
            // Non-blocking: ignore UI fetch errors here
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadBackups();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <div className="relative">
            <button
                className="p-2 rounded-md border border-transparent bg-white hover:bg-gray-50"
                onClick={() => setOpen(prev => !prev)}
                aria-label="Backups"
                title="Backups"
            >
                <MdSettingsBackupRestore className="w-5 h-5 text-gray-700" />
            </button>
            {open && (
                <>
                    <div className="absolute mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                        <div className="p-2">
                            <div className="px-3 py-2 text-xs text-gray-500 uppercase tracking-wide">Live Backups</div>
                            {loading && (
                                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                            )}
                            {!loading && backups.length === 0 && (
                                <div className="px-3 py-2 text-sm text-gray-500">No backups yet</div>
                            )}
                            <div className="max-h-80 overflow-auto">
                                {backups.map((b, idx) => (
                                    <button
                                        key={`${b.draft_name}-${idx}`}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                        onClick={() => {
                                            setOpen(false);
                                            onOpenBackup && onOpenBackup(b.draft_name);
                                        }}
                                        title={b.draft_name}
                                    >
                                        <div className="font-medium truncate">{b.draft_name}</div>
                                        {b.last_modified && (
                                            <div className="text-xs text-gray-500">
                                                {new Date(b.last_modified).toLocaleString()}
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                </>
            )}
        </div>
    );
};
